import type { AlertAck, DriverShipment } from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { Icon } from "@khazzin/ui/icons/Icon";
import { Bdi } from "@khazzin/ui/primitives/Bdi";
import { BellRing } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";

/**
 * The acknowledgment interstitial (DA-12-13, EP12-S15): an alert that asks
 * for an acknowledgment takes the whole screen — its title, the stop it is
 * about, and one 56px **Acknowledge** button — until the driver taps it.
 *
 * **One action, and focus cannot leave it.** It is an `alertdialog`; the
 * button takes focus when it opens and Tab stays on it; Escape does nothing:
 * the only way past is to acknowledge. The tap is queued (`ack_alert`), so it
 * works with no signal.
 */

export interface AckInterstitialProps {
	alert: AlertAck;
	/** The stop, when the cached day has it. */
	stop?: DriverShipment | undefined;
	/** How many more wait after this one. */
	more: number;
	t: Translate;
	onAcknowledge: (alert: AlertAck) => Promise<void>;
}

export function AckInterstitial({ alert, stop, more, t, onAcknowledge }: AckInterstitialProps) {
	const titleId = useId();
	const bodyId = useId();
	const button = useRef<HTMLButtonElement>(null);
	const [busy, setBusy] = useState(false);
	// biome-ignore lint/correctness/useExhaustiveDependencies: a new alert takes the focus again.
	useEffect(() => {
		button.current?.focus();
	}, [alert.id]);

	const trap = (event: KeyboardEvent) => {
		if (event.key === "Tab" || event.key === "Escape") {
			event.preventDefault();
			button.current?.focus();
		}
	};
	const tracking = stop?.trackingNumber ?? alert.trackingNumber;
	const urgent = alert.status === "escalated" || alert.eventType === "shipment.cancelled";

	return (
		<div
			role="alertdialog"
			aria-modal="true"
			aria-labelledby={titleId}
			aria-describedby={bodyId}
			onKeyDown={trap}
			className="fixed inset-0 z-50 flex flex-col justify-between gap-6 bg-bg-surface px-4 py-8"
		>
			{/* EP10-S21 (DA-10-15): the severity band — danger for a cancellation or an escalation. */}
			<span
				aria-hidden="true"
				data-severity={urgent ? "danger" : "brand"}
				className={`absolute inset-x-0 top-0 h-2 ${urgent ? "bg-danger-fg" : "bg-brand"}`}
			/>
			<div className="flex flex-col gap-4">
				<span
					className={
						alert.status === "escalated"
							? "flex size-12 items-center justify-center rounded-full bg-danger-bg text-danger-fg"
							: "flex size-12 items-center justify-center rounded-full bg-brand-subtle text-brand-ink"
					}
				>
					<Icon as={BellRing} size={24} />
				</span>
				<h1 id={titleId} className="text-[22px] font-extrabold text-fg">
					{t(`driver.alerts.title.${alert.eventType}`)}
				</h1>
				<div id={bodyId} className="flex flex-col gap-1 text-[15px] text-fg">
					{tracking !== undefined && (
						<p className="font-mono font-bold">
							<Bdi>{tracking}</Bdi>
						</p>
					)}
					{stop !== undefined && (
						<>
							<p>
								<Bdi>{stop.recipientName}</Bdi>
							</p>
							<p className="text-fg-muted">
								<Bdi>{stop.address}</Bdi>
							</p>
						</>
					)}
					<p className="mt-2 text-[14px] text-fg-muted">
						{t(`driver.alerts.body.${alert.eventType}`)}
					</p>
					{alert.status === "escalated" && (
						<p className="mt-2 text-[14px] font-semibold text-danger-fg">
							{t("driver.alerts.escalated")}
						</p>
					)}
				</div>
			</div>
			<div className="flex flex-col gap-2">
				{more > 0 && (
					<p className="text-center text-[13px] text-fg-muted">
						{t("driver.alerts.more", { count: more })}
					</p>
				)}
				<button
					ref={button}
					type="button"
					disabled={busy}
					onClick={() => {
						setBusy(true);
						void onAcknowledge(alert).finally(() => setBusy(false));
					}}
					className="h-14 w-full rounded-md bg-brand text-[16px] font-bold text-fg-on-brand hover:bg-brand-hover disabled:opacity-60"
				>
					{t("driver.alerts.acknowledge")}
				</button>
			</div>
		</div>
	);
}
